import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { AUTH_ROUTES } from '../constants';
import { Progress } from '@/components/ui/progress';
import { checkAllPermissions, checkAnyPermissions, PERMISSIONS } from '../permissions';

export interface ProtectedRouteProps {
  component: React.ComponentType<any>;
  permissions?: (keyof typeof PERMISSIONS)[];
  requireAny?: boolean;
}

/**
 * ProtectedRoute component - Protects routes that require authentication
 * @param component - Component to render if authenticated
 * @param permissions - Array of permissions required to access the route
 * @param requireAny - If true, requires any permission in the array (OR). Default false (AND).
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  component: Component,
  permissions = [],
  requireAny = false,
}) => {
  const { isAuth, loading, user } = useAuth();
  const userPermissions = user?.permissions || [];
  const permissionNames = permissions.map((p) => PERMISSIONS[p].name);
  const hasPermission = requireAny
    ? checkAnyPermissions(userPermissions, permissionNames)
    : checkAllPermissions(userPermissions, permissionNames);

  if (loading) {
    return <Progress value={100} className="w-full animate-pulse" />;
  }

  if (!isAuth) {
    return <Navigate to={AUTH_ROUTES.LOGIN} replace />;
  }

  if (!hasPermission) {
    return <Navigate to={AUTH_ROUTES.LOGIN} replace />;
  }

  return <Component />;
}

export default ProtectedRoute;
